import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FilterSheet } from '@/components/ui/FilterSheet';
import {
  fetchLocationGroups, createLocationGroup, updateLocationGroup, deleteLocationGroup,
} from '@/lib/api';

/**
 * Reports Advanced filters (Reporting v2 slice 1) — replaces the
 * single-select location dropdown on report shells that opt in.
 * Searchable multi-select locations (residential entries arrive
 * pre-tagged in their names), saved location groups (pick, create
 * from the current selection, rename, delete), geography selects
 * populated from the caller's own scope values, the type facet, and
 * the residential-enrollment facet. All state lives in the shell's
 * URL-backed ReportsUrlState — this component is controlled.
 */

export interface AdvancedLocationRow {
  _id: string;
  name: string;
  type?: string;
  address?: { city?: string; state?: string; country?: string };
}

export interface AdvancedFilterValue {
  locationIds: string[];
  groupId: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  locationTypes: string[];
  residentialEnrollment: 'enrolled' | 'not_enrolled' | null;
}

interface Props {
  locations: ReadonlyArray<AdvancedLocationRow>;
  value: AdvancedFilterValue;
  onChange: (next: Partial<AdvancedFilterValue>) => void;
}

const row: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const label: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
  color: 'var(--color-tx-m)',
};
const select: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 8, fontSize: 13,
  background: 'var(--color-bg)', color: 'var(--color-tx)',
  border: '1px solid var(--color-brd)',
};

export function AdvancedFilters({ locations, value, onChange }: Props) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const groupsQ = useQuery({ queryKey: ['location-groups'], queryFn: fetchLocationGroups, staleTime: 60_000 });
  const groups = groupsQ.data ?? [];

  const invalidateGroups = () => qc.invalidateQueries({ queryKey: ['location-groups'] });
  const createMut = useMutation({ mutationFn: (name: string) => createLocationGroup(name, value.locationIds), onSuccess: invalidateGroups });
  const renameMut = useMutation({
    mutationFn: (args: { id: string; name: string }) => updateLocationGroup(args.id, { name: args.name }),
    onSuccess: invalidateGroups,
  });
  const deleteMut = useMutation({ mutationFn: deleteLocationGroup, onSuccess: invalidateGroups });

  // Geo option lists come from the caller's own scoped locations, so
  // the dropdowns can only name places the caller can already see.
  const geo = useMemo(() => {
    const countries = new Set<string>();
    const states = new Set<string>();
    const cities = new Set<string>();
    const types = new Set<string>();
    for (const l of locations) {
      if (l.address?.country) countries.add(l.address.country);
      if (l.address?.state) states.add(l.address.state);
      if (l.address?.city) cities.add(l.address.city);
      if (l.type) types.add(l.type);
    }
    const sort = (s: Set<string>) => [...s].sort((a, b) => a.localeCompare(b));
    return { countries: sort(countries), states: sort(states), cities: sort(cities), types: sort(types) };
  }, [locations]);

  const visibleLocations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((l) => l.name.toLowerCase().includes(q));
  }, [locations, search]);

  const toggleLocation = (id: string) => {
    const has = value.locationIds.includes(id);
    onChange({
      locationIds: has ? value.locationIds.filter((x) => x !== id) : [...value.locationIds, id],
      // Picking individual locations detaches from a group selection.
      groupId: null,
    });
  };

  const activeCount =
    (value.locationIds.length > 0 ? 1 : 0)
    + (value.groupId ? 1 : 0)
    + (value.country ? 1 : 0)
    + (value.state ? 1 : 0)
    + (value.city ? 1 : 0)
    + (value.locationTypes.length > 0 ? 1 : 0)
    + (value.residentialEnrollment ? 1 : 0);

  return (
    <FilterSheet triggerLabel="Advanced filters" activeCount={activeCount}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 260 }}>
        {/* Saved groups */}
        <div style={row}>
          <span style={label}>Saved group</span>
          <select
            style={select}
            value={value.groupId ?? ''}
            onChange={(e) => onChange({
              groupId: e.target.value || null,
              // A group IS the selection; explicit picks clear.
              locationIds: [],
            })}
            aria-label="Saved location group"
          >
            <option value="">None</option>
            {groups.map((g) => (
              <option key={g._id} value={g._id}>{g.name} ({g.location_ids.length})</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              style={{ ...select, cursor: 'pointer' }}
              disabled={value.locationIds.length === 0 || createMut.isPending}
              onClick={() => {
                const name = window.prompt('Name this group of locations:');
                if (name?.trim()) createMut.mutate(name.trim());
              }}
            >
              Save selection as group
            </button>
            {value.groupId && (
              <>
                <button
                  type="button"
                  style={{ ...select, cursor: 'pointer' }}
                  disabled={renameMut.isPending}
                  onClick={() => {
                    const current = groups.find((g) => g._id === value.groupId);
                    const name = window.prompt('Rename group:', current?.name ?? '');
                    if (name?.trim() && value.groupId) renameMut.mutate({ id: value.groupId, name: name.trim() });
                  }}
                >
                  Rename
                </button>
                <button
                  type="button"
                  style={{ ...select, cursor: 'pointer' }}
                  disabled={deleteMut.isPending}
                  onClick={() => {
                    if (value.groupId && window.confirm('Delete this group? Reports using it fall back to no filter.')) {
                      deleteMut.mutate(value.groupId);
                      onChange({ groupId: null });
                    }
                  }}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        {/* Locations multi-select */}
        <div style={row}>
          <span style={label}>Locations</span>
          <input
            style={select}
            type="search"
            placeholder="Search locations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search locations"
          />
          <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {visibleLocations.map((l) => (
              <label key={l._id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={value.locationIds.includes(l._id)}
                  onChange={() => toggleLocation(l._id)}
                />
                <span>{l.name}</span>
              </label>
            ))}
            {visibleLocations.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--color-tx-m)' }}>No locations match.</span>
            )}
          </div>
        </div>

        {/* Geography */}
        <div style={row}>
          <span style={label}>Geography</span>
          <select style={select} value={value.country ?? ''} onChange={(e) => onChange({ country: e.target.value || null })} aria-label="Country">
            <option value="">Any country</option>
            {geo.countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select style={select} value={value.state ?? ''} onChange={(e) => onChange({ state: e.target.value || null })} aria-label="State or province">
            <option value="">Any state/province</option>
            {geo.states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={select} value={value.city ?? ''} onChange={(e) => onChange({ city: e.target.value || null })} aria-label="City">
            <option value="">Any city</option>
            {geo.cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Type facet */}
        <div style={row}>
          <span style={label}>Location type</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {geo.types.map((t) => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={value.locationTypes.includes(t)}
                  onChange={() => onChange({
                    locationTypes: value.locationTypes.includes(t)
                      ? value.locationTypes.filter((x) => x !== t)
                      : [...value.locationTypes, t],
                  })}
                />
                <span>{t}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Residential enrollment facet */}
        <div style={row}>
          <span style={label}>Residential monetization</span>
          <select
            style={select}
            value={value.residentialEnrollment ?? ''}
            onChange={(e) => onChange({
              residentialEnrollment: e.target.value === 'enrolled' || e.target.value === 'not_enrolled'
                ? e.target.value
                : null,
            })}
            aria-label="Residential enrollment"
          >
            <option value="">Any</option>
            <option value="enrolled">Enrolled</option>
            <option value="not_enrolled">Not enrolled</option>
          </select>
        </div>
      </div>
    </FilterSheet>
  );
}
